import AIRoomGame from "./AIRoomGame.tsx";
import AIRoomNameEntry from "./AIRoomNameEntry.tsx";
import AIRoomInstructions from "./AIRoomInstructions.tsx";
import AIRoomLobby from "./AIRoomLobby.tsx";
import AIRoomVote from "./AIRoomVote.tsx";
import { useState } from "react";
import { useAIRoomSocket } from "./hooks/useAIRoomSocket.js";
import WaitingForPromptsRender from "./Components/WaitingForPromptsRender.js";

interface PublicPlayerData {
  sessionId: string;
  name: string;
  charCount: number;
  hasSubmittedInstructions: boolean;
  customPromptCount: number;
}

interface VoteDistributions {
  craziness: Record<number, number>;
  drinkAmount: Record<number, number>;
  sexualScaleVotes: number[];
}

interface AIRoomProps {
  roomName: string;
  setRoomState: (state: string) => void;
  AIRoomState: string;
  setAIRoomState: (state: string) => void;
  socket: any;
  containerHeight: number;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
  fetchString: string;
  sessionId: string;
  resetSessionId: () => string;
}

function AIRoom({
  roomName,
  setRoomState,
  AIRoomState,
  setAIRoomState,
  socket,
  containerHeight,
  showInfoMessage,
  fetchString,
  sessionId,
  resetSessionId,
}: AIRoomProps) {
  const [waitingForPrompts, setWaitingForPrompts] = useState(false);
  const [players, setPlayers] = useState<PublicPlayerData[]>([]);
  const [voteDistributions, setVoteDistributions] =
    useState<VoteDistributions | null>(null);
  const [setupDone, setSetupDone] = useState(false);
  const [adminSessionId, setAdminSessionId] = useState<string | null>(null);
  const [myVote, setMyVote] = useState<{
    craziness: number;
    sexualScale: number;
    drinkAmount: number;
  } | null>(null);

  const isAdmin = !!adminSessionId && adminSessionId === sessionId;

  useAIRoomSocket(socket, roomName, sessionId, {
    setWaitingForPrompts,
    setPlayers,
    setVoteDistributions,
    setSetupDone,
    setMyVote,
    setAIRoomState,
    setAdminSessionId,
    onKicked: () => {
      resetSessionId();
      socket.emit("leave room", roomName);
      setRoomState("start screen");
      setAIRoomState("name-entry");
      showInfoMessage("warning", "Du ble kastet ut", false);
    },
  });

  return (
    <>
      {waitingForPrompts && <WaitingForPromptsRender />}

      {AIRoomState === "name-entry" && (
        <AIRoomNameEntry
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={setAIRoomState}
          setRoomState={setRoomState}
          players={players}
          showInfoMessage={showInfoMessage}
        />
      )}

      {AIRoomState === "name-entry-edit" && (
        <AIRoomNameEntry
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={setAIRoomState}
          setRoomState={setRoomState}
          players={players}
          showInfoMessage={showInfoMessage}
          isEdit={true}
        />
      )}

      {AIRoomState === "instructions" && (
        <AIRoomInstructions
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={setAIRoomState}
          showInfoMessage={showInfoMessage}
          containerHeight={containerHeight}
        />
      )}

      {AIRoomState === "edit-profile" && (
        <AIRoomInstructions
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={setAIRoomState}
          showInfoMessage={showInfoMessage}
          containerHeight={containerHeight}
          isEditProfile={true}
        />
      )}

      {AIRoomState === "lobby" && (
        <AIRoomLobby
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          players={players}
          voteDistributions={voteDistributions}
          setAIRoomState={setAIRoomState}
          setRoomState={setRoomState}
          waitingForPrompts={waitingForPrompts}
          showInfoMessage={showInfoMessage}
          fetchString={fetchString}
          myVote={myVote}
          setupDone={setupDone}
          isAdmin={isAdmin}
        />
      )}

      {AIRoomState === "vote" && (
        <AIRoomVote
          roomName={roomName.toLowerCase()}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={setAIRoomState}
          voteDistributions={voteDistributions}
          myVote={myVote}
          showInfoMessage={showInfoMessage}
        />
      )}

      {AIRoomState === "done" && (
        <AIRoomGame
          roomName={roomName.toLowerCase()}
          setRoomState={setRoomState}
          socket={socket}
          setAIRoomState={setAIRoomState}
          containerHeight={containerHeight}
          fetchString={fetchString}
        />
      )}
    </>
  );
}

export default AIRoom;
